import { useNavigate } from 'react-router-dom';
import { BinaryQuestionTemplate } from '@templates';
import { useState } from 'react';
import { ErrorMessages } from '@types';
import { booleanSchema, parseZodErrors } from '@utils';
import { ZodError } from 'zod';
import { isBinaryOptionDefault } from '@shared/utils';
import { BinaryOptionType } from '@shared/types';
import { useAnimalNuisanceStore } from '@animal-nuisance/hooks/useAnimalNuisanceStore';
import { canBeContactedQuestionData } from '@animal-nuisance/constants/questionData';
import { useHandleSubmit } from '@animal-nuisance/hooks/useHandleSubmit';
import { RoutesList } from '@animal-nuisance/routes/Routes';
import { AnimalNuisancePayload } from '../../payload/Payload';

export const ContactAboutTheIssuePage = () => {
  const navigate = useNavigate();
  const {
    data: { canBeContacted, wantConfirmationEmail },
    update,
  } = useAnimalNuisanceStore();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const questionData = canBeContactedQuestionData;
  const [submitLoading, setSubmitLoading] = useState(false);
  const errorText = 'Please specify if we can contact you about your issue';
  const { mutateAsync: submitForm, isPending } = useHandleSubmit();
  const payload = AnimalNuisancePayload();

  const options = questionData.options.map((option) => ({
    ...option,
    id: option.labelText,
    name: option.labelText,
    isLabelBold: false,
    isDefault: isBinaryOptionDefault(
      option.labelText as BinaryOptionType,
      canBeContacted,
    ),
  }));

  const handleChange = (result: string) => {
    setErrorMessages([]);
    update('canBeContacted', result === 'Yes');
  };

  const handleNext = async () => {
    setSubmitLoading(true);
    try {
      const validatedData = booleanSchema(questionData.key, errorText).parse({
        [questionData.key]: canBeContacted,
      });
      update('canBeContacted', validatedData[questionData.key]);
      if (
        validatedData[questionData.key] === false &&
        wantConfirmationEmail === false
      ) {
        payload.ReporterObject.contact_details = null;
        await submitForm(payload);
        navigate(RoutesList.CONFIRMATION);
      } else {
        navigate(RoutesList.YOUR_DETAILS);
      }
    } catch (error) {
      if (error instanceof ZodError) {
        const validationErrors = parseZodErrors(error as ZodError);
        setErrorMessages(
          validationErrors.map((error) => ({
            message: errorText,
            path: error.path,
          })),
        );
      } else {
        setErrorMessages([
          {
            message: (error as Error).message,
            path: questionData.key,
          },
        ]);
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const buttonNext = {
    label:
      canBeContacted === false && wantConfirmationEmail === false
        ? 'Submit'
        : 'Next',
    onClick: handleNext,
    loading: isPending || submitLoading,
  };

  const buttonPrevious = {
    disabled: isPending || submitLoading,
    onClick: () => {
      navigate(RoutesList.CONFIRMATION_EMAIL);
    },
  };

  return (
    <BinaryQuestionTemplate
      title={questionData.title}
      description={questionData.description}
      errorPath={questionData.key}
      errorMessages={errorMessages}
      options={options}
      onChange={handleChange}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
    />
  );
};
